<template>
  <div class="invoice-apply-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('invoiceApplyDetail.title')" :subtitle="$t('invoiceApplyDetail.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('invoiceApplyDetail.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- 业主 / 发票信息面板 -->
    <div class="ds-panel">
      <div class="ds-toolbar-like">
        <span class="ds-panel__title">{{ $t('invoiceApplyDetail.page.infoTitle') }}</span>
      </div>

      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.applyId') }}</span>
          <span class="ds-info-grid__value ds-num">{{ invoiceApplyDetailInfo.applyId || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.invoiceType') }}</span>
          <span class="ds-info-grid__value">
            <ds-status-tag :type="invoiceApplyDetailInfo.invoiceType === '1001' ? 'info' : 'primary'"
              :label="invoiceApplyDetailInfo.invoiceType === '1001' ? $t('invoiceApplyDetail.personal') : $t('invoiceApplyDetail.company')" />
          </span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.ownerName') }}</span>
          <span class="ds-info-grid__value">{{ invoiceApplyDetailInfo.ownerName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.createUserName') }}</span>
          <span class="ds-info-grid__value">
            {{ invoiceApplyDetailInfo.createUserName || '-' }}({{ invoiceApplyDetailInfo.applyTel || '' }})
          </span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.invoiceName') }}</span>
          <span class="ds-info-grid__value">{{ invoiceApplyDetailInfo.invoiceName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.invoiceNum') }}</span>
          <span class="ds-info-grid__value ds-num">{{ invoiceApplyDetailInfo.invoiceNum || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.invoiceAddress') }}</span>
          <span class="ds-info-grid__value">{{ invoiceApplyDetailInfo.invoiceAddress || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.invoiceAmount') }}</span>
          <span class="ds-info-grid__value ds-num">{{ formatMoney(invoiceApplyDetailInfo.invoiceAmount) }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.stateName') }}</span>
          <span class="ds-info-grid__value">
            <ds-status-tag type="info" :label="invoiceApplyDetailInfo.stateName || '-'" />
          </span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.createTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ formatDateTimeSec(invoiceApplyDetailInfo.createTime) }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('invoiceApplyDetail.invoiceCode') }}</span>
          <span class="ds-info-grid__value ds-num">
            {{ invoiceApplyDetailInfo.invoiceCode || $t('invoiceApplyDetail.notUploaded') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Tab 明细面板 -->
    <div class="ds-panel ds-panel--tabs">
      <el-tabs v-model="invoiceApplyDetailInfo._currentTab" @tab-click="handleTabClick">
        <el-tab-pane :label="$t('invoiceApplyDetail.feeTab')" name="invoiceApplyDetailFee">
          <invoice-apply-detail-fee v-if="invoiceApplyDetailInfo._currentTab === 'invoiceApplyDetailFee'"
            :apply-id="invoiceApplyDetailInfo.applyId"
            :owner-id="invoiceApplyDetailInfo.ownerId"></invoice-apply-detail-fee>
        </el-tab-pane>
        <el-tab-pane :label="$t('invoiceApplyDetail.eventTab')" name="invoiceApplyDetailEvent">
          <invoice-apply-detail-event v-if="invoiceApplyDetailInfo._currentTab === 'invoiceApplyDetailEvent'"
            :apply-id="invoiceApplyDetailInfo.applyId"></invoice-apply-detail-event>
        </el-tab-pane>
        <el-tab-pane :label="$t('invoiceApplyDetail.imageTab')" name="invoiceApplyDetailJpg">
          <div v-if="invoiceApplyDetailInfo.urls && invoiceApplyDetailInfo.urls.length > 0" class="ds-invoice-links">
            <a v-for="(item, index) in invoiceApplyDetailInfo.urls" :key="index" target="_blank" :href="item"
              class="ds-invoice-links__item">
              <i class="el-icon-picture-outline"></i>
              {{ $t('invoiceApplyDetail.downloadInvoice') }}
            </a>
          </div>
          <div v-else class="ds-empty-text">{{ $t('invoiceApplyDetail.noInvoice') }}</div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import { getInvoiceApplyDetail } from '@/api/fee/invoiceApplyDetailApi'
import InvoiceApplyDetailFee from '@/components/fee/invoiceApplyDetailFee'
import InvoiceApplyDetailEvent from '@/components/fee/invoiceApplyDetailEvent'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'InvoiceApplyDetailList',
  components: {
    InvoiceApplyDetailFee,
    InvoiceApplyDetailEvent,
    DsPageHeader,
    DsStatusTag
  },
  data() {
    return {
      invoiceApplyDetailInfo: {
        applyId: '',
        invoiceType: '',
        ownerName: '',
        createUserName: '',
        invoiceName: '',
        invoiceNum: '',
        invoiceAddress: '',
        invoiceAmount: '',
        stateName: '',
        createTime: '',
        invoiceCode: '',
        urls: [],
        _currentTab: 'invoiceApplyDetailFee'
      }
    }
  },
  created() {
    this.invoiceApplyDetailInfo.applyId = this.$route.query.applyId
    if (this.invoiceApplyDetailInfo.applyId) {
      this.loadInvoiceApplyInfo()
    }
  },
  methods: {
    async loadInvoiceApplyInfo() {
      try {
        const params = {
          page: 1,
          row: 1,
          applyId: this.invoiceApplyDetailInfo.applyId
        }
        const res = await getInvoiceApplyDetail(params)
        if (res.code === 0 && res.data && res.data.length > 0) {
          Object.assign(this.invoiceApplyDetailInfo, res.data[0])
        }
      } catch (error) {
        this.$message.error(this.$t('invoiceApplyDetail.fetchError'))
      }
    },
    handleTabClick(tab) {
      this.invoiceApplyDetailInfo._currentTab = tab.name
    },
    goBack() {
      this.$router.go(-1)
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.invoice-apply-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-md);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 信息面板标题条 */
.ds-toolbar-like {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
  border-bottom: 1px solid var(--ds-gray-200);
}

/* 信息栅格 */
.ds-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--ds-spacing-md) var(--ds-spacing-lg);
  padding: var(--ds-spacing-lg);

  &__item {
    display: flex;
    align-items: baseline;
    gap: var(--ds-spacing-sm);
    min-width: 0;
  }

  &__label {
    flex-shrink: 0;
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
  }

  &__value {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-900);
    word-break: break-all;
  }
}

/* Tab 面板 */
.ds-panel--tabs {
  padding: 0 var(--ds-spacing-lg) var(--ds-spacing-lg);
}

/* 发票链接列表 */
.ds-invoice-links {
  display: flex;
  flex-direction: column;
  gap: var(--ds-spacing-sm);
  padding: var(--ds-spacing-md) 0;

  &__item {
    color: var(--ds-primary);
    text-decoration: none;
    font-size: var(--ds-font-size-sm);

    &:hover {
      text-decoration: underline;
    }
  }
}

/* 空文案 */
.ds-empty-text {
  padding: var(--ds-spacing-lg) 0;
  color: var(--ds-gray-500);
  font-size: var(--ds-font-size-sm);
  text-align: center;
}
</style>
